import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import Event from "sap/ui/base/Event";
import EventProvider from "sap/ui/base/EventProvider";
/**
 * User Model
 * This class represents a singleton user model that holds user-related data.
 * It provides methods to get and set user properties such as username, token, roles, etc.
 * @namespace sap.mcsuite.model.ts
 */
export default class User extends EventProvider {
    private authenticated: boolean;
    private active: boolean;
    private username: string;
    private token: string;
    private refreshToken: string;
    private idToken: string;
    private scopes: string[];
    private email: string;
    private firstName: string;
    private lastName: string;
    private locale: string;
    private id: number;
    private static instance: User;

    private constructor() {
        super();
        this.authenticated = false;
        this.active = false;
        this.username = '';
        this.token = '';
        this.refreshToken = '';
        this.idToken = '';
        this.scopes = [];
        this.firstName = '';
        this.lastName = '';
        this.email = '';
        this.locale = 'en'; // Default locale
        this.id = 0; // Default ID
    }


    public static getInstance(): User {
        if (!User.instance) {
            User.instance = new User();
        }
        return User.instance;
    }

    public isAuthenticated(): boolean {
        return this.authenticated;
    }

    public isActive(): boolean {
        return this.active;
    }

    public getUsername(): string {
        return this.username;
    }

    public getToken(): string {
        return this.token;
    }

    public getRefreshToken(): string {
        return this.refreshToken;
    }

    public getIdToken(): string {
        return this.idToken;
    }

    public getScopes(): string[] {
        return this.scopes;
    }

    public getEmail(): string {
        return this.email;
    }

    public getFirstName(): string {
        return this.firstName;
    }

    public getLastName(): string {
        return this.lastName;
    }

    public getLocale(): string {
        return this.locale;
    }

    public authenticate(): User {
        this.authenticated = true;
        this.fireAuthenticated();
        return this;
    }

    public setUsername(username: string): User {
        this.username = username;
        return this;
    }

    public setToken(token: string): User {
        this.token = token;
        return this;
    }

    public setRefreshToken(refreshToken: string): User {
        this.refreshToken = refreshToken;
        return this;
    }

    public setIdToken(idToken: string): User {
        this.idToken = idToken;
        return this;
    }

    public setScopes(scopes: string[]): User {
        this.scopes = scopes;
        return this;
    }

    public setEmail(email: string): User {
        this.email = email;
        return this;
    }

    public setFirstName(firstName: string): User {
        this.firstName = firstName;
        return this;
    }

    public setLastName(lastName: string): User {
        this.lastName = lastName;
        return this;
    }

    public setLocale(locale: string): User {
        this.locale = locale;
        return this;
    }

    public setId(id: number): User {
        this.id = id;
        return this;
    }

    public getId(): number {
        return this.id;
    }

    public async requestIsActive(model: ODataModel): Promise<boolean> {
        try {
            const sStatus = await model.bindProperty(`/authuser(${this.id})/status`).requestValue() as string;  
            if(sStatus === 'Active') {
                this.active = true;
                return true;
            }
            return false;
        } catch (error) {
            console.error("Failed to check if user is active:", error);
            return false;
        }
    }


    public async requestScopes(model: ODataModel): Promise<void> {
        const listBinding = model.bindList(`/authuser(${this.id})/cfr_users_dt`, null, null, null, {
            $filter: "status lt 90",
            $expand: "_role_id($expand=cfr_roles_dt($expand=_action))",
            $select: "_role_id",
        });
        const team = await model.bindProperty(`/authuser(${this.id})/team`).requestValue() as string;
        const contexts = await listBinding.requestContexts(0, 1000);
        let scopeObjects = await Promise.all(
            contexts.map(context => context.requestObject() as { _role_id?: { cfr_roles_dt?: Array<{ _action?: {code?: string}, status?: number}> } })
        );
        this.scopes = scopeObjects.map(roleObject => {
            const scopes: Array<string> = roleObject._role_id?.cfr_roles_dt?.map(role => role._action.code && role.status < 90 ? role._action.code : '') ?? [];
            return scopes;
        }).flat();
        this.scopes.push(team.toLowerCase() === 'admin' ? 'admin' : 'user');

        console.log("Scopes assigned to user:", this.scopes);
    }
    /**
     *  Checks if the user has a specific scope.
     * @param scopePart - The scope to check.
     * @returns True if the user has the scope, false otherwise.
     */
    public hasScopePart(scopePart: string): boolean {
        if (!scopePart) return false;
        if (this.scopes.includes('admin')) return true;
        return this.scopes.some(scope => scope.includes(scopePart));
    }

    /**
     * Checks if the user has any scope that matches the given pattern.
     * All patterns are treated as regex patterns
     * @param scopePattern - The regex pattern to match
     * @returns True if the user has any matching scope
     */
    public hasScopeRegex(scopePattern: string): boolean {
        if (this.scopes.includes('admin')) return true;
        try {
            const regex = new RegExp(`^${scopePattern}$`);
            
            // Check if any user scope matches the pattern
            return this.scopes.some(userScope => regex.test(userScope));
        } catch (error) {
            console.warn(`Invalid regex pattern: ${scopePattern}`, error);
            return false;
        }
    }
    
    public attachAuthenticated(callback: (event: Event) => void, listener?: object): User {
        this.attachEvent("authenticated", callback, listener);
        return this;
    }

    public fireAuthenticated(): void {
        this.fireEvent("authenticated", { isAuthenticated: this.authenticated });
    }
}